/**
 * Словарь экрана ситуаций: как называется сигнал, линза, основание, диспозиция.
 *
 * Почему словарь здесь, а не в разметке
 * -------------------------------------
 *
 * Одно и то же название встречается на экране трижды — вкладкой линзы,
 * отметкой строки и подписью раскрытия, — и три копии разошлись бы на первой же
 * правке. Отдельный модуль ещё и проверяем без рендера: полнота словаря по
 * перечислению контракта — это тест на равенство множеств, а не на верстку.
 *
 * Чего здесь нет
 * --------------
 *
 * Ни одного правила, решающего, ЧТО показывать. Порог существенности, вид
 * сигнала, состав линз и порядок строк считает сервер по
 * ``docs/model/situations-rules.yaml``; клиент их не выводит и не уточняет.
 * Второе место, где написано «это существенно», означало бы два разных ответа
 * на один вопрос — и разошлись бы они молча.
 *
 * И ни одной оценки. Вид сигнала — это НАЗВАНИЕ совпадения фактов («срок
 * прошёл, отчёта нет»), а не суждение о том, кто виноват.
 */

import type {
  SituationDispositionBrief,
  SituationGround,
  SituationLens,
  SituationRow,
} from "../../api/client";
import type { DataStatus } from "../../components/StatusMark";
import { dateLong } from "../../astra/lib/format";
import { defineMessages, type PlainMessage } from "../../i18n/define";
import { tr } from "../../i18n/runtime";

const m = defineMessages({
  lensAll: { id: 'situations.lens.all', defaultMessage: 'Все сигналы', description: 'Вкладка очереди ситуаций: все сигналы, прошедшие порог существенности; коротко, рядом число' },
  lensAllQ: { id: 'situations.lens.all.question', defaultMessage: 'Что прошло порог существенности на этом срезе', description: 'Подсказка по наведению на вкладку «Все сигналы»: на какой вопрос отвечает взгляд' },
  lensAny: { id: 'situations.lens.any', defaultMessage: 'Любой сигнал', description: 'Вкладка очереди ситуаций: хозяйства хотя бы с одним сигналом; коротко' },
  lensAnyQ: { id: 'situations.lens.any.question', defaultMessage: 'У кого есть хотя бы один сигнал: сроки, вода, расхождения или карантин', description: 'Подсказка по наведению на вкладку «Любой сигнал»' },
  lensDeadlines: { id: 'situations.lens.deadlines', defaultMessage: 'Сроки', description: 'Вкладка очереди ситуаций: просроченные и скоро закрывающиеся окна работ; коротко' },
  lensDeadlinesQ: { id: 'situations.lens.deadlines.question', defaultMessage: 'Где после срока нет отчёта или окно скоро закроется', description: 'Подсказка по наведению на вкладку «Сроки»' },
  lensCombined: { id: 'situations.lens.combined', defaultMessage: 'Нет отчёта и воды', description: 'Вкладка очереди ситуаций: нет ни отчёта, ни подтверждения полива; коротко' },
  lensCombinedQ: { id: 'situations.lens.combined.question', defaultMessage: 'Где нет ни отчёта, ни подтверждения подачи', description: 'Подсказка по наведению на вкладку «Нет отчёта и воды»' },
  lensWater: { id: 'situations.lens.water', defaultMessage: 'Вода', description: 'Вкладка очереди ситуаций: разрывы по воде; коротко' },
  lensWaterQ: { id: 'situations.lens.water.question', defaultMessage: 'Где окно закрылось, а подача его не закрыла', description: 'Подсказка по наведению на вкладку «Вода»' },
  lensVerification: { id: 'situations.lens.verification', defaultMessage: 'Проверка', description: 'Вкладка очереди ситуаций: расхождения отчёта и проверки; коротко' },
  lensVerificationQ: { id: 'situations.lens.verification.question', defaultMessage: 'Где расходятся отчёт и проверка или проверка ещё не проведена', description: 'Подсказка по наведению на вкладку «Проверка»' },
  lensWindow: { id: 'situations.lens.window', defaultMessage: 'Окно', description: 'Вкладка очереди ситуаций: окно работ скоро закроется; коротко' },
  lensWindowQ: { id: 'situations.lens.window.question', defaultMessage: 'Где окно закроется в ближайшие дни', description: 'Подсказка по наведению на вкладку «Окно»' },
  lensQuarantine: { id: 'situations.lens.quarantine', defaultMessage: 'Карантин', description: 'Вкладка очереди ситуаций: карантинные статусы и предписания; коротко' },
  lensQuarantineQ: { id: 'situations.lens.quarantine.question', defaultMessage: 'Где контуры хозяйства в красном или жёлтом статусе или предписание после срока', description: 'Подсказка по наведению на вкладку «Карантин»' },
  kindConflict: { id: 'situations.kind.conflict', defaultMessage: 'расхождение', description: 'Отметка вида сигнала в строке очереди ситуаций: отчёт и проверка говорят разное; бейдж, одно слово' },
  kindConflictMeaning: { id: 'situations.kind.conflict.meaning', defaultMessage: 'Отчёт и проверка говорят разное. Обе записи остаются; разрешает спор человек, а не пересчёт.', description: 'Подсказка по наведению на отметку «расхождение»' },
  kindOverdue: { id: 'situations.kind.overdue', defaultMessage: 'просрочка', description: 'Отметка вида сигнала в строке очереди: окно закрылось, отчёта нет; бейдж, одно слово' },
  kindOverdueMeaning: { id: 'situations.kind.overdue.meaning', defaultMessage: 'Окно закрылось, отчёта нет, и таких работ больше порога существенности.', description: 'Подсказка по наведению на отметку «просрочка»' },
  kindWater: { id: 'situations.kind.water', defaultMessage: 'разрыв по воде', description: 'Отметка вида сигнала в строке очереди: окно полива закрылось без подтверждения подачи; бейдж' },
  kindWaterMeaning: { id: 'situations.kind.water.meaning', defaultMessage: 'Окно полива закрылось, а подача его не закрыла: независимого подтверждения нет.', description: 'Подсказка по наведению на отметку «разрыв по воде»' },
  kindCombined: { id: 'situations.kind.combined', defaultMessage: 'просрочка и вода', description: 'Отметка вида сигнала в строке очереди: нет ни отчёта, ни подтверждения подачи; бейдж' },
  kindCombinedMeaning: { id: 'situations.kind.combined.meaning', defaultMessage: 'Нет ни отчёта после срока, ни подтверждения подачи — два молчания на одной работе.', description: 'Подсказка по наведению на отметку «просрочка и вода»' },
  kindWindow: { id: 'situations.kind.window', defaultMessage: 'окно закрывается', description: 'Отметка вида сигнала в строке очереди: до конца окна остались дни; бейдж' },
  kindWindowMeaning: { id: 'situations.kind.window.meaning', defaultMessage: 'До конца окна остались дни, а отчёта ещё нет.', description: 'Подсказка по наведению на отметку «окно закрывается»' },
  kindPending: { id: 'situations.kind.pending', defaultMessage: 'без отметки проверяющего', description: 'Отметка устаревшего вида сигнала (уже записанные диспозиции): отчёт принят, проверяющий не отметил; бейдж' },
  kindPendingMeaning: { id: 'situations.kind.pending.meaning', defaultMessage: 'Отчёт принят как факт; отметки проверяющего нет. Это не нарушение и не долг.', description: 'Подсказка по наведению на отметку «без отметки проверяющего»' },
  kindQuarantine: { id: 'situations.kind.quarantine', defaultMessage: 'карантин', description: 'Отметка вида сигнала в строке очереди: карантинные статусы или предписание; бейдж, одно слово' },
  kindQuarantineMeaning: { id: 'situations.kind.quarantine.meaning', defaultMessage: 'Контуры хозяйства в красном или жёлтом карантинном статусе либо с предписанием после срока.', description: 'Подсказка по наведению на отметку «карантин»' },
  kindUnknownMeaning: { id: 'situations.kind.unknown.meaning', defaultMessage: 'Вид сигнала не из словаря этого клиента: смысл неизвестен.', description: 'Подсказка по наведению на отметку вида сигнала, который экран не знает; показывается вместе с кодом вида' },
  groundObligation: { id: 'situations.ground.obligation', defaultMessage: 'работа на поле #{id}', description: 'Основание сигнала в раскрытии строки (ссылка): работа на поле; {id} — номер работы' },
  groundWaterLine: { id: 'situations.ground.waterLine', defaultMessage: 'позиция заявки на воду #{id}', description: 'Основание сигнала в раскрытии строки: позиция заявки на воду; {id} — номер записи' },
  groundWaterDelivery: { id: 'situations.ground.waterDelivery', defaultMessage: 'операция водоподачи #{id}', description: 'Основание сигнала в раскрытии строки: операция водоподачи; {id} — номер записи' },
  groundVerification: { id: 'situations.ground.verification', defaultMessage: 'суждение проверяющего #{id}', description: 'Основание сигнала в раскрытии строки: суждение проверяющего; {id} — номер записи' },
  groundOther: { id: 'situations.ground.other', defaultMessage: '{kind} #{id}', description: 'Основание сигнала неизвестного вида; {kind} — код вида от сервера, {id} — номер записи' },
  dispNoticed: { id: 'situations.disposition.noticed', defaultMessage: 'заметили', description: 'Статус разбора ситуации (бейдж в строке очереди): человек увидел сигнал' },
  dispResponded: { id: 'situations.disposition.responded', defaultMessage: 'отреагировали', description: 'Статус разбора ситуации (бейдж в строке очереди): человек принял меры' },
  dispClosed: { id: 'situations.disposition.closed', defaultMessage: 'закрыли', description: 'Статус разбора ситуации (бейдж в строке очереди): ситуация закрыта' },
  signalUnknown: { id: 'situations.signal.unknown', defaultMessage: 'величина не посчитана', description: 'Значение сигнала в строке очереди, когда сервер не вернул число' },
  signalOf: { id: 'situations.signal.of', defaultMessage: '{value} из {denominator}', description: 'Величина сигнала в строке очереди и её знаменатель: «9 из 45»' },
  deadlineNone: { id: 'situations.deadline.none', defaultMessage: 'срока нет', description: 'Подпись срока в строке очереди ситуаций, когда ближайшего срока нет' },
  deadlinePassed: { id: 'situations.deadline.passed', defaultMessage: 'срок прошёл {date}', description: 'Подпись срока в строке очереди: срок уже прошёл на дату среза; {date} — дата срока' },
  deadlineNext: { id: 'situations.deadline.next', defaultMessage: 'ближайший срок {date}', description: 'Подпись срока в строке очереди: срок ещё впереди; {date} — дата срока' },
});

/* ------------------------------------------------------------------ */
/* Линзы                                                               */
/* ------------------------------------------------------------------ */

export interface LensSpec {
  id: SituationLens;
  /** Короткая подпись вкладки — сообщение каталога. */
  label: PlainMessage;
  /** На какой вопрос отвечает этот взгляд. Живёт в подсказке. */
  question: PlainMessage;
}

/**
 * Порядок линз — порядок чтения, а не алфавит.
 *
 * Первой стоит «Все сигналы», и это не «всё подряд»: сервер отбирает в неё
 * строки, прошедшие ПОРОГ существенности. Без порога в очередь попадает почти
 * весь район — «отчёт есть, проверки нет» верно почти везде, — и для этого
 * случая заведена своя линза.
 */
export const LENSES: readonly LensSpec[] = [
  {
    id: "all",
    label: m.lensAll,
    question: m.lensAllQ,
  },
  {
    id: "any",
    label: m.lensAny,
    question: m.lensAnyQ,
  },
  {
    id: "deadlines",
    label: m.lensDeadlines,
    question: m.lensDeadlinesQ,
  },
  {
    id: "combined",
    label: m.lensCombined,
    question: m.lensCombinedQ,
  },
  {
    id: "water",
    label: m.lensWater,
    question: m.lensWaterQ,
  },
  {
    id: "verification",
    label: m.lensVerification,
    question: m.lensVerificationQ,
  },
  {
    id: "window",
    label: m.lensWindow,
    question: m.lensWindowQ,
  },
  {
    id: "quarantine",
    label: m.lensQuarantine,
    question: m.lensQuarantineQ,
  },
];

export function lensSpec(lens: string): LensSpec {
  return LENSES.find((item) => item.id === lens) ?? LENSES[0]!;
}

/**
 * Линза, с которой открывать очередь из блока внимания.
 *
 * Очередь внимания говорит, ЧЕГО в районе много; очередь ситуаций — К КОМУ
 * ехать. Переход между ними обязан сохранять вопрос: человек, нажавший «спор о
 * данных», ждёт расхождений, а не общего списка. Ключи здесь — те, что печатает
 * `serving.overview_queries`; незнакомый ключ честно даёт общую линзу, а не
 * придуманную.
 */
export function lensForAttention(key: string | null | undefined): SituationLens {
  switch (key) {
    case "work_conflict":
    case "work_needs_clarification":
      return "verification";
    case "water":
      return "water";
    case "work_overdue":
      return "all";
    default:
      return "all";
  }
}

/* ------------------------------------------------------------------ */
/* Виды сигнала                                                        */
/* ------------------------------------------------------------------ */

export interface KindSpec {
  /** Подпись отметки. Короткая: строка очереди узкая. */
  label: string;
  /** Что именно совпало. Уезжает в подсказку отметки. */
  meaning: string;
  /**
   * Состояние из словаря отметок. Красный здесь означает КРИТИЧНОЕ СОСТОЯНИЕ
   * данных (окно закрылось, два утверждения не сходятся), а не плохое
   * хозяйство: сводной оценки субъекта в этом корпусе не существует.
   */
  status: DataStatus;
}

const KINDS: Record<string, { label: PlainMessage; meaning: PlainMessage; status: DataStatus }> = {
  report_conflict: {
    label: m.kindConflict,
    meaning: m.kindConflictMeaning,
    status: "critical",
  },
  overdue_material: {
    label: m.kindOverdue,
    meaning: m.kindOverdueMeaning,
    status: "critical",
  },
  water_gap_after_window: {
    label: m.kindWater,
    meaning: m.kindWaterMeaning,
    status: "water",
  },
  combined_overdue_and_water: {
    label: m.kindCombined,
    meaning: m.kindCombinedMeaning,
    status: "critical",
  },
  window_due_soon: {
    label: m.kindWindow,
    meaning: m.kindWindowMeaning,
    status: "warning",
  },
  // Сервер этот вид больше не выдаёт (учётная рамка, решение №28); подпись
  // оставлена для уже записанных диспозиций.
  verification_pending: {
    label: m.kindPending,
    meaning: m.kindPendingMeaning,
    status: "progress",
  },
  quarantine: {
    label: m.kindQuarantine,
    meaning: m.kindQuarantineMeaning,
    status: "warning",
  },
};

export function kindSpec(kind: string): KindSpec {
  const known = KINDS[kind];
  if (known) {
    return { label: tr(known.label), meaning: tr(known.meaning), status: known.status };
  }
  return {
    label: kind,
    // Неизвестный код НЕ повышается до знакомого: сервер назвал вид, которого
    // клиент не знает, и врать про его смысл хуже, чем сказать об этом.
    meaning: tr(m.kindUnknownMeaning),
    status: "missing",
  };
}

/* ------------------------------------------------------------------ */
/* Основания                                                           */
/* ------------------------------------------------------------------ */

export function groundLabel(ground: SituationGround): string {
  const id = ground.ref_id;
  switch (ground.kind) {
    case "obligation":
      return tr(m.groundObligation, { id });
    case "water_line":
      return tr(m.groundWaterLine, { id });
    case "water_delivery":
      return tr(m.groundWaterDelivery, { id });
    case "verification":
      return tr(m.groundVerification, { id });
    default:
      return tr(m.groundOther, { kind: ground.kind, id });
  }
}

/**
 * Уровень, НА КОТОРЫЙ ведёт основание, либо `null` — если своего экрана у него
 * нет.
 *
 * Основание — это ссылка на строку корпуса, и «ссылка, которую нельзя
 * открыть» отличается от текста ровно ничем: сигнал, чьё основание не
 * проверить, проверить нечем. У обязательства такой экран есть — работа
 * адресуема как `/works/:id`, и `ref_id` обязательства и есть её номер. У
 * позиции заявки, операции подачи и суждения проверяющего своего адреса
 * сегодня нет, и рисовать им ссылку в никуда хуже, чем не рисовать: человек
 * поверил бы, что разбор там есть.
 */
export function groundLevel(ground: SituationGround): "work" | null {
  return ground.kind === "obligation" ? "work" : null;
}

/* ------------------------------------------------------------------ */
/* Диспозиция                                                          */
/* ------------------------------------------------------------------ */

export interface DispositionSpec {
  label: string;
  /** Кто и когда. Пустая строка — автор не записан (такого не бывает у людей). */
  who: string;
  tone: "neutral" | "attention";
}

const STATUSES: Record<string, { label: PlainMessage; tone: "neutral" | "attention" }> =
  {
    noticed: { label: m.dispNoticed, tone: "attention" },
    responded: { label: m.dispResponded, tone: "neutral" },
    closed: { label: m.dispClosed, tone: "neutral" },
  };

export function dispositionSpec(
  disposition: SituationDispositionBrief,
): DispositionSpec {
  const known = STATUSES[disposition.status];
  const author =
    disposition.closed_by ?? disposition.responded_by ?? disposition.noticed_by;
  return {
    label: known ? tr(known.label) : disposition.status,
    who: author ?? "",
    tone: known ? known.tone : "neutral",
  };
}

/**
 * Какие жесты доступны из текущего состояния.
 *
 * Таблица повторяет серверную (``situations.enums.ALLOWED_TRANSITIONS``), и это
 * осознанное повторение ПОДСКАЗКИ, а не правила: отказывает сервис, а кнопка,
 * предлагающая невозможный переход, отправляет человека за отказом. Отсутствие
 * диспозиции — не состояние: там доступно ровно «заметили».
 */
export function nextGestures(
  disposition: SituationDispositionBrief | null | undefined,
): ReadonlyArray<"notice" | "respond" | "close"> {
  if (!disposition) return ["notice"];
  switch (disposition.status) {
    case "noticed":
      return ["respond", "close"];
    case "responded":
      return ["close"];
    default:
      return [];
  }
}

/* ------------------------------------------------------------------ */
/* Числа строки                                                        */
/* ------------------------------------------------------------------ */

/**
 * «N из M» — величина сигнала и ЕГО знаменатель.
 *
 * Знаменатель едет рядом всегда: «четыре из четырёхсот» и «четыре из четырёх» —
 * разные утверждения, и общий итог вместо своего знаменателя сделал бы все
 * сигналы одинаково незначительными.
 */
export function signalText(row: SituationRow): string {
  const value = row.signal.measured.value;
  if (value === null) return tr(m.signalUnknown);
  return tr(m.signalOf, { value, denominator: row.signal.denominator });
}

/**
 * Есть ли среди оснований строки синтетика.
 *
 * Считается ОТ ДАННЫХ, а не от флага «показывать демо»: флаг говорит, что
 * синтетике разрешили участвовать, а не что она участвовала.
 */
export function hasSyntheticGrounds(row: SituationRow): boolean {
  return (
    row.epistemic === "synthetic_demo" ||
    row.grounds.some((ground) => ground.epistemic === "synthetic_demo")
  );
}

/** Сколько строк выдачи опирается на синтетику. Ноль — плашка не нужна. */
export function syntheticRows(rows: ReadonlyArray<SituationRow>): number {
  return rows.filter(hasSyntheticGrounds).length;
}

/* ------------------------------------------------------------------ */
/* Срок строки                                                         */
/* ------------------------------------------------------------------ */

/**
 * Что сказать о `next_deadline` — и сказать это ЧЕСТНО относительно среза.
 *
 * Поле считается как ближайший конец окна среди обязательств без отчёта — по
 * всем, а не только по закрывающимся. Значит, у строки, которая попала в
 * очередь просрочкой, оно закономерно лежит В ПРОШЛОМ, и слово «ближайший»
 * там читается как «предстоящий»: человек видит дату рядом с датой среза и
 * понимает её наоборот.
 *
 * Поэтому текст развёрнут по сравнению с горизонтом, а не по виду сигнала:
 * сравнение — это то же наблюдение о данных, и делается оно там, где дата
 * превращается в слова, а не в голове у читателя. Контракт при этом не
 * трогается: поле остаётся тем же.
 */
export function deadlineText(
  nextDeadline: string | null | undefined,
  horizon: string | null | undefined,
): string {
  if (!nextDeadline) return tr(m.deadlineNone);
  const shown = dateLong(nextDeadline);
  if (horizon && nextDeadline < horizon) return tr(m.deadlinePassed, { date: shown });
  return tr(m.deadlineNext, { date: shown });
}
